import cx from "classnames";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { Button, Col, Container, Row } from "reactstrap";
import qs from "qs";
import PropTypes from "prop-types";

import { Form } from "../ui/form";
import { Input } from "../ui/input";

import signInPng from "../../assets/images/dhp-sign-in.png";
import signInWebp from "../../assets/images/dhp-sign-in.webp";
import global from "../../scss/dhp.scss";

import {
  clearCurrentUserSession,
  login,
  resendVerificationCode,
  send2StepSigninOTP,
} from "../../store/actions/authActions";
import { Icon } from "../ui/icon";
import Image from "../ui/image";
import LoadingBar, { showLoading, hideLoading } from "react-redux-loading-bar";
import DocHipoLogo from "./DocHipoLogo";
import { fetchCookie, getTargetRedirectionEndpoint, removeCookie } from "../../_helpers/utils";
import GoogleButton from "./GoogleButton";
import { INVALID_SIGNIN, INVALID_SIGNIN_MSG, SIGNIN } from "../../constants/user";
import useWebsiteService from "../../hooks/useWebsiteService";

let style = Object.assign({}, global);

const Login = ({ location }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    loggedIn: isLoggedIn,
    error: loginError,
    loading: isLoading,
    user: userInfo,
  } = useSelector(state => state?.auth);

  // Below Custom hook for show website template/asset view
  const { isWebsiteView, webComponent } = useWebsiteService();

  let initialState = {
    email: {
      label: "Email",
      name: "email",
      value: "",
      type: "email",
      validation: {
        required: true,
      },
      valid: false,
      touched: false,
      showError: "",
    },

    password: {
      label: "Password",
      name: "password",
      value: "",
      type: "password",
      validation: {
        required: true,
      },
      valid: false,
      touched: false,
      showError: "",
    },

    remember_me: {
      label: "Remember me",
      name: "remember_me",
      value: false,
      type: "checkbox",
      validation: {
        required: false,
      },
      valid: true,
    },
  };

  const [state, setState] = useState(initialState);
  const [passwordVisible, setPasswordVisibility] = useState(false);
  const [isFormValid, setFormValid] = useState(false);
  const [isBtnClick, setIsBtnClick] = useState(false);
  const [isProcessDone, setIsProcessDone] = useState(false);
  const [showErrorMsg, setShowErrorMsg] = useState(false);
  const [errorMsgInfo, setErrorMsgInfo] = useState();

  const emailReg = new RegExp(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,15}/g);
  const passwordReg = new RegExp("(?=.{6,})");
  const checkValidity = ({ value, validation, type }) => {
    let isValid = true;
    if (validation.required) isValid = value.trim() !== "" && isValid;

    if (type === "email") isValid = emailReg.test(value.trim()) && isValid;
    if (type === "password") isValid = passwordReg.test(value.trim()) && isValid;

    return isValid;
  };

  const handleChange = (evt, field) => {
    setShowErrorMsg(false);
    let updatedForm = { ...state };
    if (updatedForm[field].type === "checkbox") updatedForm[field].value = evt.target.checked;
    else updatedForm[field].value = evt.target.value;

    updatedForm[field].valid = checkValidity(updatedForm[field]);

    if (updatedForm[field].valid) updatedForm[field].showError = "";
    else if (updatedForm[field].touched) {
      setErrorMessage(field);
    }
    setState(updatedForm);
    checkFormValidity();
  };

  const setErrorMessage = field => {
    let updatedForm = { ...state };
    updatedForm[field].touched = true;
    if (!updatedForm[field].valid) {
      if (updatedForm[field].value.length < 1) {
        updatedForm[field].showError = `Please enter ${updatedForm[field].label}`;
      } else if (updatedForm[field].type === "email") {
        updatedForm[field].showError = `Invalid format of email`;
      } else if (updatedForm[field].type === "password") {
        updatedForm[field].showError = `Password must be minimum 6 characters`;
      }
    }

    setState(updatedForm);
  };

  const focusOutValidation = field => {
    setErrorMessage(field);
  };

  const handleSubmit = evt => {
    if (isFormValid && !process.env.MAINTENANCE) {
      evt.preventDefault();
      setIsBtnClick(true);
      dispatch(
        login({ email: state.email.value, password: state.password.value, remember_me: state.remember_me.value })
      ).then(() => setIsProcessDone(true));
    }
  };

  const checkFormValidity = () => setFormValid(Object.keys(state).every(field => state[field].valid));

  const togglePasswordVisiblity = () => {
    setPasswordVisibility(passwordVisible ? false : true);
  };

  const goto = e => {
    e.preventDefault();
    if (qs.parse(location?.search?.substring(1))?.target) {
      history.push(`/signup?target=${qs.parse(location.search.substring(1)).target}`);
    } else history.push("/signup");
  };

  useEffect(() => {
    if (isLoggedIn) {
      // Clear all auth component if redux isLoggedin true but no access_token in local storage
      if (!localStorage.getItem("access_token")) dispatch(clearCurrentUserSession());

      if (qs.parse(location?.search?.substring(1))?.target) {
        // check available target endpoint
        let targetPageEndpoint = getTargetRedirectionEndpoint(qs.parse(location.search.substring(1)).target);
        if (targetPageEndpoint?.includes("/templates?uasset=")) {
          history.push(targetPageEndpoint);
        } else {
          const exactPath = targetPageEndpoint?.split("?");
          if (exactPath[1]) {
            history.push({ pathname: exactPath[0], search: `?${exactPath[1]}`, state: { redirectFromPublic: true } });
          } else {
            history.push({ pathname: targetPageEndpoint, state: { redirectFromPublic: true } });
          }
        }
      } else history.push("/");
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoading) dispatch(showLoading());
    else dispatch(hideLoading());
  }, [isLoading]);

  useEffect(() => {
    if (isProcessDone && userInfo?.otp_token) {
      dispatch(send2StepSigninOTP(userInfo?.otp_token));
      if (qs.parse(location?.search?.substring(1))?.target) {
        history.push(
          `/signin/${userInfo.otp_token}/authenticate?target=${qs.parse(location.search.substring(1)).target}`
        );
      } else history.push(`/signin/${userInfo.otp_token}/authenticate`);
    }
  }, [isProcessDone, isLoading]);

  // Redirect to signup verification if not verified user trying to login (Changes for new API)
  useEffect(() => {
    if (userInfo?.otp_verified === false && userInfo?.id) {
      dispatch(resendVerificationCode(userInfo?.id));
      history.push("/signup/verification");
    }

    // Redirect to signup final if user verified but company not updated through signup final (Changes for new API)
    if (userInfo?.first_login === false && userInfo?.company_updated === false) {
      history.push("/signup-final");
    }
  }, [userInfo]);

  useEffect(() => {
    if (loginError) {
      setErrorMsgInfo(loginError.message);
      setShowErrorMsg(true);
    }
  }, [loginError]);

  // Check Cookie for federate signup
  useEffect(() => {
    let cookieValue = fetchCookie("_dh_signin_user");
    if (cookieValue) {
      try {
        let coockiedata = cookieValue.replace(/'/g, '"');
        coockiedata = coockiedata.replace(/\\054/g, ",");
        coockiedata = coockiedata.replace(/\\/g, "");
        JSON.parse(coockiedata);
      } catch (error) {
        setIsProcessDone(true);

        setErrorMsgInfo(cookieValue);
        setShowErrorMsg(true);
        removeCookie("_dh_signin_user");
      }
    }
  }, []);

  return (
    <section className={style["entry-form"]}>
      <Container cssModule={style} fluid>
        <Row cssModule={style}>
          <Col xs="12" cssModule={style}>
            <div className={style["logo-wraper"]}>
              <DocHipoLogo />
            </div>
          </Col>

          <Col cssModule={style} lg="6" className={cx(style["d-none"], style["d-lg-block"])}>
            <div
              className={cx(
                style["left-panel"],
                style["d-flex"],
                style["align-items-center"],
                style["justify-content-end"]
              )}>
              {isWebsiteView === false && <Image WebpImage={signInWebp} PngImage={signInPng} />}
              {isWebsiteView && webComponent}
            </div>
          </Col>
          <Col cssModule={style} lg="6" xs="12">
            <div
              className={cx(
                style["right-panel"],
                style["d-flex"],
                style["align-items-center"],
                style["justify-content-center"]
              )}>
              <Form
                onSubmit={handleSubmit}
                className={cx(
                  style["form-wraper"],
                  style["bg-white"],
                  style["rounded-lg"],
                  style["shadow-lg"],
                  style["animated"],
                  style["zoomIn"]
                )}>
                <h1 className={cx(style["mb-4"], style["pb-3"])}>Sign in</h1>

                <GoogleButton label={"Sign in with Google"} mod={SIGNIN} />

                <p className={cx(style["or"])}>
                  <span>or</span>
                </p>

                <Input
                  label={state.email.label}
                  type={state.email.type}
                  value={state.email.value}
                  onChange={e => handleChange(e, "email")}
                  onBlur={() => focusOutValidation("email")}
                  required={true}
                  showerror={state.email.showError}
                />

                <Input
                  label={state.password.label}
                  cssModule={style}
                  type={passwordVisible ? "text_password" : "password"}
                  value={state.password.value}
                  onChange={e => handleChange(e, "password")}
                  onBlur={() => focusOutValidation("password")}
                  required={true}
                  toggleHandler={togglePasswordVisiblity}
                  showerror={state.password.showError}
                />

                <div
                  className={cx(
                    style["form-group"],
                    style["d-flex"],
                    style["align-items-center"],
                    style["justify-content-between"]
                  )}>
                  <div className={cx(style["custom-control"], style["custom-checkbox"])}>
                    <input
                      type={state.remember_me.type}
                      value={state.remember_me.value}
                      className={cx(style["custom-control-input"])}
                      id="customCheck1"
                      onChange={e => handleChange(e, "remember_me")}
                    />
                    <label className={cx(style["custom-control-label"])} htmlFor="customCheck1">
                      Remember me
                    </label>
                  </div>
                  {!process.env.MAINTENANCE && (
                    <h6 className={cx(style["m-0"], style["text-right"])}>
                      <Link to="/forgot-password" className={style["forgot-pass"]}>
                        Forgot password?
                      </Link>
                    </h6>
                  )}
                </div>
                {isProcessDone && errorMsgInfo && showErrorMsg && (
                  <div className={cx(style["alert"], style["alert-block"], style["alert-danger"])}>
                    <span>{errorMsgInfo === INVALID_SIGNIN ? INVALID_SIGNIN_MSG : errorMsgInfo}</span>
                  </div>
                )}

                {!process.env.MAINTENANCE && (
                  <Button
                    type="submit"
                    color="primary"
                    cssModule={style}
                    className={cx(style["btn-block"], style["btn-loader"], style["my-2"], {
                      [style["btn-loaded"]]: isLoading,
                    })}
                    disabled={!isFormValid || isLoading}>
                    {isBtnClick && <LoadingBar className={style["btn-loader-bg"]} />}
                    Sign In <Icon icon="arrow-right" additionalclass="ml-2" />{" "}
                  </Button>
                )}
                {process.env.MAINTENANCE && (
                  <Button color="primary" cssModule={style} className={cx(style["btn-block"])} disabled={true}>
                    We’ll be Back Soon
                  </Button>
                )}
                <div className={cx(style["text-center"], style["pt-3"], style["animated"], style["fadeInUp"])}>
                  <h6 className={cx(style["signup-link-text"], style["m-0"])}>
                    Don’t have an account?{" "}
                    <Link to="" onClick={e => goto(e)}>
                      Sign up
                    </Link>
                  </h6>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

Login.propTypes = {
  location: PropTypes.object,
};

export default Login;
